<script setup lang="ts">
import type { Schemas } from '#shopware'
import { getProductRoute } from '@shopware/helpers'

interface SidenavWishlistProps {
    showCloseButton?: boolean
    maxHeight?: string
    emptyWishlistMessage?: string
}

const props = withDefaults(defineProps<SidenavWishlistProps>(), {
    showCloseButton: true,
    maxHeight: 'calc(100vh - 200px)',
    emptyWishlistMessage: ''
})

// Global sidenav state management
const { isOpen } = useSidenav()
const wishlistOpen = isOpen('wishlist')

// i18n
const localePath = useLocalePath()
const { formatLink } = useInternationalization(localePath)

// Initialize data and state
const { getWishlistProducts, items, count, wishlistProducts, loadWishlistProducts } = useWishlist()
const isLoading = ref(false)

// Navigate to wishlist page and close sidenav
const handleViewWishlist = () => {
    navigateTo(formatLink('/wishlist'))
    wishlistOpen.value = false
}

// Navigate to product detail page and close sidenav
const handleProductClick = (product: Schemas['Product']) => {
    navigateTo(formatLink(getProductRoute(product)))
    wishlistOpen.value = false
}

// Watch for open/close to fetch data
watch(wishlistOpen, (newValue) => {
    if (newValue) {
        getWishlistProducts()
        loadWishlistProducts()
    }
})

// Watch for changes in wishlist items and refresh product list
watch(items, () => {
    if (wishlistOpen.value) {
        loadWishlistProducts()
    }
})

// Automatically fetch on component mount if opened
onMounted(() => {
    if (wishlistOpen.value) {
        getWishlistProducts()
        loadWishlistProducts()
    }
})
</script>

<template>
    <SidenavOverlay
        v-model="wishlistOpen"
        direction="right"
        width-class="w-full md:w-[600px]"
    >
        <div class="flex flex-col h-full bg-base-100">
            <!-- Header -->
            <SidenavHeader
                :title="$t('wishlist.title')"
                :show-close-button="showCloseButton"
                :close-aria-label="$t('wishlist.sidenav.close')"
                @close="wishlistOpen = false"
            />

            <!-- Loading state -->
            <div v-if="isLoading" class="flex items-center justify-center flex-grow p-8">
                <div class="animate-spin rounded-full h-8 w-8 border-b-2 border-border" />
            </div>

            <!-- Empty wishlist state -->
            <div
                v-else-if="!count || count === 0"
                class="flex flex-col items-center justify-center flex-grow p-8 bg-base-200"
            >
                <FoundationIcon name="heart" class="w-14 h-14 mb-4 text-base-500" />
                <p class="text-2xl font-bold mb-4 text-center">{{ props.emptyWishlistMessage || $t('wishlist.emptyWishlistLabel') }}</p>

                <FoundationButton
                    color="primary"
                    @click="wishlistOpen = false"
                >
                    {{ $t('wishlist.discover') }}
                </FoundationButton>
            </div>

            <!-- Wishlist items -->
            <div v-else class="overflow-auto" :style="{ 'max-height': props.maxHeight }">
                <div class="border-b border-border px-6 md:px-9 py-3 text-sm font-bold">
                    {{ count }} {{ $t('wishlist.products') }}
                </div>

                <div>
                    <ProductListItem
                        v-for="product in wishlistProducts"
                        :key="product.id"
                        :product="product"
                        variant="wishlist"
                        class="px-6 md:px-9 py-6 border-b border-border last:border-b-0"
                        @product-click="handleProductClick"
                    />
                </div>
            </div>

            <!-- Footer with action button -->
            <div v-if="count && count > 0" class="grid grid-cols-1 sm:grid-cols-2 gap-4 px-6 md:px-9 py-4 border-t border-border">
                <div>
                    <FoundationButton
                        color="secondary"
                        class="w-full"
                        @click="wishlistOpen = false"
                    >
                        {{ $t('wishlist.sidenav.continueShopping') }}
                    </FoundationButton>
                </div>

                <div>
                    <FoundationButton
                        color="primary"
                        class="w-full"
                        @click="handleViewWishlist"
                    >
                        {{ $t('wishlist.sidenav.viewWishlist') }}
                    </FoundationButton>
                </div>
            </div>
        </div>
    </SidenavOverlay>
</template>
